// FILE: src/components/law/LawArticleAuditorPanel.tsx
// PHOENIX PROTOCOL - AUDITOR PANEL V3.0.2
//
// V3.0.2: FIX TS2540 — `RefObject.current` është readonly në React 19.
//         Cast-i te MutableRefObject para assign.
//
// V3.0.1: FIX TS2322 — callback ref në vend të refObject direkt.
// V3.0: FIX T10-T19 (auditim i plotë).

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BrainCircuit, X, AlertCircle, Sparkles, Send, Loader2, Trash2, RotateCcw, Play,
} from 'lucide-react';
import { ChatMessage, SUGGESTED_QUESTIONS } from './lawArticleTypes';
import type { TFunction } from 'i18next';

interface LawArticleAuditorPanelProps {
  isOpen: boolean;
  summaryContent: string;
  isSummarizing: boolean;
  summaryError: string;
  cleanSummary: string;
  messages: ChatMessage[];
  showSuggestions: boolean;
  isAuditing: boolean;
  chatError: string | null;
  inputQuery: string;
  onInputQueryChange: (val: string) => void;
  onSendQuery: (query?: string) => void;
  onCloseAuditor: () => void;
  onStartAnalysis?: () => void;
  onClearCache?: () => void;
  onReanalyze?: () => void;
  summarySectionRef?: React.Ref<HTMLDivElement>;
  chatPanelRef?: React.Ref<HTMLDivElement>;
  chatContainerRef?: React.Ref<HTMLDivElement>;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
  t: TFunction;
}

export const LawArticleAuditorPanel: React.FC<LawArticleAuditorPanelProps> = ({
  isOpen,
  summaryContent,
  isSummarizing,
  summaryError,
  cleanSummary,
  messages,
  showSuggestions,
  isAuditing,
  chatError,
  inputQuery,
  onInputQueryChange,
  onSendQuery,
  onCloseAuditor,
  onStartAnalysis,
  onClearCache,
  onReanalyze,
  chatContainerRef,
  inputRef,
  t,
}) => {
  const [mounted, setMounted] = useState(false);
  const localTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollableBodyRef = useRef<HTMLDivElement | null>(null);

  // ═══════════════════════════════════════════════════════════════════════
  // V3.0.2: Callback ref — cast parent RefObject te MutableRefObject për assign
  // ═══════════════════════════════════════════════════════════════════════

  const setTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      localTextareaRef.current = node;
      if (inputRef && 'current' in inputRef) {
        // React 19 tipon current si readonly; cast-i është i sigurt sepse
        // parent ref e ka krijuar me useRef() që është mutable në runtime.
        (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      }
    },
    [inputRef],
  );

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════
  // AUTO-SCROLL
  // ═══════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!scrollableBodyRef.current) return;
    const el = scrollableBodyRef.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (nearBottom && (isSummarizing || isAuditing)) {
      el.scrollTop = el.scrollHeight;
    }
  }, [summaryContent, messages, isSummarizing, isAuditing]);

  // ═══════════════════════════════════════════════════════════════════════
  // ESCAPE KEY
  // ═══════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseAuditor();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onCloseAuditor]);

  if (!isOpen || !mounted) return null;

  // ═══════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onInputQueryChange(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 130)}px`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!inputQuery.trim() || isAuditing) return;
      onSendQuery();
      if (localTextareaRef.current) {
        localTextareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleSendClick = () => {
    if (!inputQuery.trim() || isAuditing) return;
    onSendQuery();
    if (localTextareaRef.current) {
      localTextareaRef.current.style.height = 'auto';
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // TYPOGRAPHY RENDERER
  // ═══════════════════════════════════════════════════════════════════════

  const renderCleanTypography = (text: string) => {
    if (!text) return null;
    const lines = text.split('\n');
    const headingPattern = /^(?:📌|⚖️|⚠️|🔗|🏛️|📖|#+\s)/;

    return lines.map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} className="h-3" />;

      if (headingPattern.test(trimmed)) {
        const cleanTitle = trimmed.replace(/^###\s*/, '').replace(/\*\*/g, '');
        return (
          <h4
            key={i}
            className="text-sm sm:text-base md:text-lg font-bold text-text-primary mt-6 mb-2.5 flex items-center gap-2 border-b border-main pb-2"
          >
            {cleanTitle}
          </h4>
        );
      }

      if (/^[-•*]\s/.test(trimmed)) {
        const itemText = trimmed.replace(/^[-•*]\s*/, '').replace(/\*\*/g, '');
        return (
          <li
            key={i}
            className="ml-4 sm:ml-6 text-xs sm:text-sm md:text-[15px] text-text-secondary leading-relaxed list-disc mb-2 font-sans"
          >
            {itemText}
          </li>
        );
      }

      const cleanParagraph = trimmed.replace(/\*\*/g, '');
      return (
        <p
          key={i}
          className="text-xs sm:text-sm md:text-[15px] text-text-secondary leading-relaxed font-sans mb-3.5 text-left"
        >
          {cleanParagraph}
        </p>
      );
    });
  };

  // ═══════════════════════════════════════════════════════════════════════
  // MODAL
  // ═══════════════════════════════════════════════════════════════════════

  const modalContent = (
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 md:p-6 lg:p-8 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auditor-panel-title"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCloseAuditor}
          className="fixed inset-0 bg-black/85 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative z-10 w-full max-w-5xl xl:max-w-6xl bg-card border border-main rounded-2xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden text-text-primary"
        >
          <div className="px-6 sm:px-8 py-4 sm:py-5 border-b border-main flex items-center justify-between bg-surface shrink-0">
            <div className="flex items-center gap-3.5">
              <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-xl bg-primary-start/10 border border-primary-start/20 flex items-center justify-center text-primary-start shrink-0">
                <BrainCircuit size={22} />
              </div>
              <div>
                <h3
                  id="auditor-panel-title"
                  className="text-xs sm:text-base font-black uppercase tracking-wider text-text-primary"
                >
                  {t('lawArticle.auditorTitle', 'Auditimi Inteligjent i Nenit')}
                </h3>
                <p className="text-[11px] sm:text-xs text-text-muted">
                  {t('lawArticle.auditorSubtitle', 'Analizë dhe këshillim ligjor i verifikuar')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-2.5">
              {onReanalyze && summaryContent && !isSummarizing && (
                <button
                  type="button"
                  onClick={onReanalyze}
                  className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center rounded-xl bg-card text-text-muted hover:text-primary-start hover:bg-primary-start/10 border border-main transition-all cursor-pointer shadow-xs"
                  title={t('lawArticle.reanalyze', 'Rianalizo nenin nga e para')}
                >
                  <RotateCcw size={16} />
                </button>
              )}

              {onClearCache && summaryContent && (
                <button
                  type="button"
                  onClick={onClearCache}
                  className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center rounded-xl bg-card text-text-muted hover:text-danger-start hover:bg-danger-start/10 border border-main transition-all cursor-pointer shadow-xs"
                  title={t('lawArticle.clearCache', 'Shlyej analizën nga memoria')}
                >
                  <Trash2 size={16} />
                </button>
              )}

              <button
                type="button"
                onClick={onCloseAuditor}
                className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center rounded-xl bg-card hover:bg-hover border border-main text-text-muted hover:text-text-primary transition-all cursor-pointer shadow-xs"
                aria-label={t('general.close', 'Mbyll')}
              >
                <X size={17} />
              </button>
            </div>
          </div>

          <div
            ref={scrollableBodyRef}
            className="px-6 sm:px-10 py-6 sm:py-8 overflow-y-auto custom-scrollbar flex-1 space-y-6 sm:space-y-8"
          >
            <div>
              {summaryError && (
                <div className="bg-danger-start/10 border border-danger-start/20 rounded-xl p-4 text-danger-start text-xs sm:text-sm font-semibold flex items-center gap-2 mb-4">
                  <AlertCircle size={17} /> {summaryError}
                </div>
              )}

              {!summaryContent && !isSummarizing && (
                <div className="py-12 px-6 border border-dashed border-main rounded-3xl flex flex-col items-center justify-center text-center bg-surface/40">
                  <div className="h-14 w-14 rounded-2xl bg-primary-start/10 border border-primary-start/20 flex items-center justify-center text-primary-start mb-4">
                    <Sparkles size={28} />
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-text-primary mb-1.5">
                    {t('lawArticle.startAnalysisTitle', 'Analizë dhe Interpretim me AI')}
                  </h4>
                  <p className="text-xs sm:text-sm text-text-muted max-w-lg mb-6 leading-relaxed">
                    {t('lawArticle.startAnalysisHint', 'Kliko butonin e mëposhtëm për të gjeneruar analizën e thellë ligjore të këtij neni, ose bëj një pyetje direkte në fushën e chat-it.')}
                  </p>

                  {onStartAnalysis && (
                    <button
                      type="button"
                      onClick={onStartAnalysis}
                      className="h-11 px-6 flex items-center gap-2.5 rounded-xl bg-primary-start hover:bg-primary-start/90 text-white text-xs sm:text-sm font-bold uppercase tracking-wider transition-all shadow-lg hover-lift cursor-pointer"
                    >
                      <Play size={15} fill="currentColor" />
                      <span>{t('lawArticle.startAnalysis', 'Fillo Analizën e Nenit')}</span>
                    </button>
                  )}
                </div>
              )}

              {isSummarizing && !summaryContent && (
                <div className="space-y-4 py-6">
                  <div className="flex items-center gap-2.5 text-primary-start text-xs sm:text-sm font-bold mb-4">
                    <Loader2 size={17} className="animate-spin" />
                    <span>{t('lawArticle.analyzingText', 'Sokrati duke analizuar...')}</span>
                  </div>
                  <div className="h-4 bg-surface rounded-lg w-full animate-pulse" />
                  <div className="h-4 bg-surface rounded-lg w-5/6 animate-pulse" />
                  <div className="h-4 bg-surface rounded-lg w-4/6 animate-pulse" />
                </div>
              )}

              {summaryContent && (
                <div className="space-y-2.5 max-w-[110ch]">
                  {renderCleanTypography(cleanSummary)}
                  {isSummarizing && (
                    <span className="inline-block w-2 h-4 bg-primary-start animate-pulse ml-1 align-middle" />
                  )}
                </div>
              )}

              {summaryContent && (
                <div className="mt-6 pt-4 border-t border-main flex items-center gap-2 text-xs text-text-muted font-medium">
                  <Sparkles size={14} className="text-primary-start shrink-0" />
                  <span>{t('lawArticle.aiDisclaimer', 'Analizë e ruajtur në memorie për të gjitha pajisjet.')}</span>
                </div>
              )}
            </div>

            {summaryContent && (
              <div className="pt-6 border-t border-main">
                <div ref={chatContainerRef} className="space-y-3.5 mb-2">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                    >
                      <div
                        className={`w-full max-w-[85%] p-4 rounded-2xl text-xs sm:text-sm shadow-xs ${
                          msg.role === 'user'
                            ? 'bg-primary-start/15 border border-primary-start/30 text-text-primary'
                            : 'bg-surface border border-main text-text-primary'
                        }`}
                      >
                        {msg.role === 'auditor' ? (
                          <div className="leading-relaxed max-w-[100ch]">
                            {renderCleanTypography(msg.content) || (
                              <span className="inline-block w-2 h-4 bg-primary-start animate-pulse" />
                            )}
                          </div>
                        ) : (
                          <p className="font-medium whitespace-pre-wrap">{msg.content}</p>
                        )}
                        <p className="text-[10px] mt-1.5 text-text-muted">
                          {msg.timestamp.toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  ))}

                  {showSuggestions && messages.length === 0 && SUGGESTED_QUESTIONS.length > 0 && (
                    <div className="flex flex-col gap-2 mt-3">
                      <p className="text-xs text-text-muted font-bold uppercase tracking-wider">
                        {t('lawArticle.suggestedQuestions', 'Pyetje të sugjeruara')}:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {SUGGESTED_QUESTIONS.map((question: string, idx: number) => (
                          <button
                            key={idx}
                            onClick={() => onSendQuery(question)}
                            className="text-xs bg-surface border border-main hover:border-primary-start/50 text-text-secondary px-3.5 py-2 rounded-xl transition-all text-left cursor-pointer"
                            type="button"
                          >
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {isAuditing && (
                    <div className="flex justify-start">
                      <div className="bg-surface border border-main p-3 rounded-2xl flex gap-1.5">
                        <span
                          className="w-1.5 h-1.5 bg-primary-start rounded-full animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        />
                        <span
                          className="w-1.5 h-1.5 bg-primary-start rounded-full animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <span
                          className="w-1.5 h-1.5 bg-primary-start rounded-full animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    </div>
                  )}

                  {chatError && (
                    <div className="bg-danger-start/10 border border-danger-start/30 rounded-xl p-3 text-danger-start text-xs flex items-center gap-2">
                      <AlertCircle size={15} /> {chatError}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 sm:p-5 bg-surface border-t border-main shrink-0">
            <div className="flex gap-2.5 items-end max-w-5xl mx-auto w-full">
              <textarea
                ref={setTextareaRef}
                value={inputQuery}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder={t('lawArticle.queryPlaceholder', 'Bëj një pyetje konkrete për këtë nen...')}
                rows={1}
                style={{ height: '44px', minHeight: '44px', maxHeight: '130px' }}
                className="flex-1 px-4 py-2.5 bg-input border border-main rounded-xl text-xs sm:text-sm resize-none overflow-y-auto custom-scrollbar text-text-primary focus:border-primary-start outline-none transition-all placeholder:text-text-muted"
                disabled={isAuditing}
              />
              <button
                type="button"
                onClick={handleSendClick}
                disabled={!inputQuery.trim() || isAuditing}
                className="h-[44px] w-[44px] flex items-center justify-center rounded-xl bg-primary-start text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-start/90 transition-all shadow-md cursor-pointer shrink-0"
                aria-label={t('lawArticle.sendMessage', 'Dërgo mesazhin')}
              >
                {isAuditing ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default LawArticleAuditorPanel;